import { useEffect, useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import { Save, Check, ShieldAlert } from 'lucide-react';

interface ConfigRow {
  key: string;
  value: string;
  updated_at: string;
}

async function hashSecret(secret: string): Promise<string> {
  const data = new TextEncoder().encode(secret);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function AdminSettings() {
  const [config, setConfig] = useState<ConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSecret, setNewSecret] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const { data } = await supabase.from('admin_config').select('*');
    const rows = (data as ConfigRow[]) || [];
    setConfig(rows);
    setAdminEmail(rows.find((r) => r.key === 'admin_email')?.value || '');
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const now = new Date().toISOString();

    const updates: Promise<unknown>[] = [
      supabase
        .from('admin_config')
        .update({ value: adminEmail, updated_at: now })
        .eq('key', 'admin_email'),
    ];

    if (newSecret.length > 0) {
      const hashed = await hashSecret(newSecret);
      updates.push(
        supabase
          .from('admin_config')
          .update({ value: hashed, updated_at: now })
          .eq('key', 'ADMIN_SECRET'),
      );
    }

    await Promise.all(updates);

    setSaving(false);
    setSaved(true);
    setNewSecret('');
    await loadConfig();
    setTimeout(() => setSaved(false), 3000);
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--dash-border)',
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Admin Settings</h1>

      <div className="max-w-lg space-y-5">
        <div
          className="rounded p-5"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>
            Authentication
          </h2>

          <div className="space-y-5">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>Admin Email</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors"
                style={inputStyle}
              />
              <p className="font-mono text-[10px] mt-1.5" style={{ color: 'var(--dash-text-faint)' }}>
                Only this email can access the admin dashboard.
              </p>
            </div>

            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-dim)' }}>Set New Security Code</label>
              <input
                type="password"
                value={newSecret}
                onChange={(e) => setNewSecret(e.target.value)}
                placeholder="Leave blank to keep current"
                className="w-full rounded px-4 py-3 font-sans text-sm focus:outline-none transition-colors"
                style={inputStyle}
              />
              <div className="flex items-start gap-1.5 mt-1.5">
                <ShieldAlert size={10} className="mt-0.5 shrink-0" style={{ color: 'var(--dash-text-faint)' }} />
                <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                  Write-only. The secret is stored as a SHA-256 hash and cannot be retrieved. Enter a new value to change it.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          className="rounded p-5"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>
            Configuration History
          </h2>
          <div className="space-y-2">
            {config.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: '1px solid var(--dash-border-light)' }}
              >
                <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{row.key}</span>
                <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                  Updated {new Date(row.updated_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-white text-zinc-900 font-mono text-sm font-medium px-6 py-3 rounded hover:bg-zinc-200 transition-colors disabled:opacity-50 uppercase tracking-wider"
        >
          {saved ? (
            <>
              <Check size={14} />
              Saved
            </>
          ) : saving ? (
            'Saving...'
          ) : (
            <>
              <Save size={14} />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
}
